import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { getData } from "../../../webService/webservice.js";
import { TextField } from "@mui/material";
import { toast } from "react-toastify";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend
);

const ProfitPage = ({ sessionToken }) => {
    const [profitLoss, setProfitLoss] = useState({});
    const [graph, setGraph] = useState(() => []);

    const [dateRange, setDateRange] = useState("Today");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const getDateRange = (range) => {
        const now = new Date();
        let start = null,
            end = null;

        switch (range) {
            case "Today":
                start = end = now;
                break;
            case "Yesterday":
                start = new Date(now);
                start.setDate(start.getDate() - 1);
                end = start;
                break;
            case "Last 7 Days":
                end = now;
                start = new Date(now);
                start.setDate(now.getDate() - 7);
                break;
            case "Current Month":
                start = new Date(now.getFullYear(), now.getMonth(), 1);
                end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            case "Last Month":
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                end = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            case "Current FY":
                start = new Date(now.getFullYear(), 3, 1);
                end = new Date(now.getFullYear() + 1, 2, 31);
                break;
            case "Previous FY":
                start = new Date(now.getFullYear() - 1, 3, 1);
                end = new Date(now.getFullYear(), 2, 31);
                break;
            default:
                return { startDate, endDate };
        }

        return {
            startDate: start.toISOString().split("T")[0],
            endDate: end.toISOString().split("T")[0],
        };
    };

    useEffect(() => {
        fetchProfitLoss();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, startDate, endDate]);

    useEffect(() => {
        // ensure graph is always an array after profitLoss changes
        if (!profitLoss || !Array.isArray(profitLoss.graph)) {
            setGraph([]);
        }
    }, [profitLoss]);

    const fetchProfitLoss = async () => {
        try {
            let params = {};

            if (dateRange === "Custom") {
                if (startDate) params.startDate = startDate;
                if (endDate) params.endDate = endDate;
            } else {
                const { startDate: s, endDate: e } = getDateRange(dateRange);
                params.startDate = s;
                params.endDate = e;
            }

            const response = await getData("reports/reports/profitloss", params, sessionToken);

            // always normalize values to avoid undefined during render
            const data = response?.data || {};
            setProfitLoss(data);
            setGraph(Array.isArray(data.graph) ? data.graph : []);
        } catch (error) {
            toast.error("Failed to load Profit & Loss Report");
            // keep graph as array in case of error
            setGraph([]);
        }
    };

    const safeMap = (arr, fn) => (Array.isArray(arr) ? arr.map(fn) : []);

    // memoize chart data so Chart.js receives a stable object reference
    const chartData = useMemo(() => {
        const labels = safeMap(graph, (g) => g.date);

        return {
            labels,
            datasets: [
                {
                    label: "Sales (₹)",
                    data: safeMap(graph, (g) => g.sales || 0),
                    borderColor: "rgba(0, 224, 150, 1)",
                    backgroundColor: "rgba(0, 224, 150, 0.18)",
                    tension: 0.7,
                    fill: true,
                    borderWidth: 3,
                    pointRadius: 0,
                },
                {
                    label: "Purchase (₹)",
                    data: safeMap(graph, (g) => g.purchase || 0),
                    borderColor: "rgba(255, 136, 0, 1)",
                    backgroundColor: "rgba(255, 136, 0, 0.18)",
                    tension: 0.7,
                    fill: true,
                    borderWidth: 3,
                    pointRadius: 0,
                },
                {
                    label: "Expenses (₹)",
                    data: safeMap(graph, (g) => g.expense || 0),
                    borderColor: "rgba(156, 39, 176, 1)",
                    backgroundColor: "rgba(156, 39, 176, 0.18)",
                    tension: 0.7,
                    fill: true,
                    borderWidth: 3,
                    pointRadius: 0,
                },
                {
                    label: "Profit (₹)",
                    data: safeMap(graph, (g) => g.profit || 0),
                    borderColor: "rgba(30, 136, 229, 1)",
                    backgroundColor: "rgba(30, 136, 229, 0.18)",
                    tension: 0.7,
                    fill: true,
                    borderWidth: 3,
                    pointRadius: 0,
                },
                {
                    label: "Loss (₹)",
                    data: safeMap(graph, (g) => g.loss || 0),
                    borderColor: "rgba(255, 69, 96, 1)",
                    backgroundColor: "rgba(255, 69, 96, 0.18)",
                    tension: 0.7,
                    fill: true,
                    borderWidth: 3,
                    pointRadius: 0,
                },
            ],
        };
    }, [graph]);

    const lineOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: "top",
                labels: { usePointStyle: true },
            },
            tooltip: {
                callbacks: {
                    label: (ctx) => `₹${ctx.raw?.toLocaleString?.() ?? ctx.raw}`,
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: "rgba(0,0,0,0.1)" },
                ticks: {
                    callback: (value) => `₹${value}`,
                },
            },
            x: {
                grid: { display: false },
            },
        },
    };

    return (
        <div className="summary-layout1">
            <div className="summary-content1">
                <main className="summary-main1">
                    {/* FILTER BAR */}
                    <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                        <TextField
                            select
                            label="Date Range"
                            value={dateRange}
                            onChange={(e) => {
                                setDateRange(e.target.value);
                                if (e.target.value !== "Custom") {
                                    const { startDate: s, endDate: ed } = getDateRange(e.target.value);
                                    setStartDate(s);
                                    setEndDate(ed);
                                }
                            }}
                            SelectProps={{ native: true }}
                        >
                            <option value="Today">Today</option>
                            <option value="Yesterday">Yesterday</option>
                            <option value="Last 7 Days">Last 7 Days</option>
                            <option value="Last Month">Last Month</option>
                            <option value="Current Month">Current Month</option>
                            <option value="Current FY">Current FY</option>
                            <option value="Previous FY">Previous FY</option>
                            <option value="Custom">Custom Date</option>
                        </TextField>

                        {dateRange === "Custom" && (
                            <>
                                <TextField
                                    label="Start Date"
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />

                                <TextField
                                    label="End Date"
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </>
                        )}
                    </div>

                    {/* GRAPH */}
                    <div style={{ marginTop: "30px", background: "#fff", padding: "20px", borderRadius: "10px" }}>
                        <h3>Profit & Loss Graph</h3>

                        {Array.isArray(graph) && graph.length > 0 ? (
                            <Line data={chartData} options={lineOptions} />
                        ) : (
                            <p style={{ textAlign: "center", padding: "20px", color: "#888" }}>No data available for this date range</p>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default ProfitPage;
